import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MapPin, Map, ChevronRight } from "lucide-react";
import { queryClient } from "@/hooks/use-query-client";

import PageContainer from "./containers/page-container";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import Dropdown from "./components/drop-down";
import type {
  NPIFreeTextType,
  ProviderFreeTextType,
  ProviderSearchResultItemType,
  SpecialtyFreeTextType,
} from "@/types";
import SearchAutocomplete from "./components/search-autocomplete";
import type { PlaceMarker } from "@/model/place";
import type Place from "@/model/place";
import { useLocationSearch } from "@/hooks/use-cost-estimator";
import useUserInfo from "@/hooks/use-user-info";
import type Distance from "@/model/distance";
import {
  DISTANCE_OPTIONS,
  PROVIDER_SEARCH_FREETEXT_TYPE,
  PROVIDER_SEARCH_RESULT_ITEM_REQUIRED_KEYS,
  PROVIDER_SEARCH_TYPES,
  PROVIDER_SETTING_TYPE,
  QueryKeys,
} from "@/constants";
import type { RootState } from "@/store/store";
import {
  setDistance,
  setLocation,
  setProviderSearchDynamicPayload,
  setViewMoreDialog,
} from "@/store/provider-search/provider-search-slice";
import GroupedSearchAutocomplete from "./components/grouped-search-autocomplete";
import {
  useFreeTextSearch,
  useProviderSearch,
  useProviderSearchSetting,
} from "@/hooks/use-provider-search";

import { capitalizeInitialLetter } from "@/lib/string-util";
import ProviderSearchFreeTextViewMore from "./components/provider-search/freetext-viewmore";
import ProviderDetails from "./components/provider-search/provider-details";
import RightSidebar from "./components/right-sidebar";
import MapView from "./components/provider-search/mapview";
import ProviderSearchResultSkeleton from "./components/provider-search/provider-search-result-skeleton";
import NoProvidersFound from "./components/provider-search/no-providers-found";
import { getGoogleMapsDirectionUrl } from "@/lib/map-utils";

const hasResultKeySet = (arr: unknown[]): boolean =>
  arr.some(
    (obj) =>
      typeof obj === "object" &&
      obj !== null &&
      PROVIDER_SEARCH_RESULT_ITEM_REQUIRED_KEYS.every((key) => key in obj),
  );

const ProviderSearchResult = () => {
  const dispatch = useDispatch();
  const userInfo = useUserInfo();
  const locationSearchQuery = useLocationSearch<Place>(userInfo);
  const {
    distance,
    category,
    location,
    specialty,
    searchCriteria,
    taxonomyCode,
    dynamicPayload,
    searchType,
    viewMoreDialog,
  } = useSelector((state: RootState) => state.providerSearch);
  const { data: settingData } = useProviderSearchSetting();
  const {
    data: providerSearchResult,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch,
  } = useProviderSearch();
  const searchResultSet =
    providerSearchResult?.pages.flatMap((page) => page.results) ?? [];
  const isEmpty = !isLoading && searchResultSet.length === 0;
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const [openMapView, setOpenMapView] = useState<{
    isMapViewOpen: boolean;
    index: number | null;
  }>({
    isMapViewOpen: false,
    index: null,
  });

  useEffect(() => {
    if (
      location !== null &&
      searchType !== null &&
      (dynamicPayload?.category !== "" ||
        dynamicPayload?.searchCriteria !== "" ||
        dynamicPayload?.speciality !== "" ||
        dynamicPayload?.taxonomyCode !== "") &&
      ((searchType === PROVIDER_SEARCH_TYPES.QUICK_SEARCH &&
        dynamicPayload?.category !== "" &&
        dynamicPayload?.searchCriteria !== "" &&
        dynamicPayload?.taxonomyCode !== "") ||
        (searchType === PROVIDER_SEARCH_TYPES.FREETEXT_SEARCH &&
          dynamicPayload?.searchCriteria !== "") ||
        (searchType === PROVIDER_SEARCH_TYPES.SEARCH_BY_SPECIALTY &&
          dynamicPayload?.category !== "" &&
          dynamicPayload?.speciality !== ""))
    ) {
      queryClient.removeQueries({ queryKey: [QueryKeys.PROVIDER_SEARCH] });
      refetch();
    }
  }, [distance, location, dynamicPayload, refetch, searchType]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries[0].isIntersecting;
        if (isVisible && hasNextPage && !isFetching && !isLoading) {
          fetchNextPage();
        }
      },
      { threshold: 1 },
    );
    const current = loadMoreRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [hasNextPage, fetchNextPage, isFetching, isLoading]);

  const openSelectedProviderInMapview = (index: number) => {
    setOpenMapView({
      isMapViewOpen: true,
      index,
    });
  };

  const getDirection = (providerLat: number, providerLng: number) => {
    if (!location) return;
    const user = { lat: location.lat, lng: location.lng };
    const provider = { lat: providerLat, lng: providerLng };
    const url = getGoogleMapsDirectionUrl(user, provider, "driving");
    window.open(url, "_blank");
  };

  const callProvider = (tel: string) => {
    window.location.href = `tel:${tel}`;
  };

  const renderSearchResult = () => {
    if (isLoading) {
      return <ProviderSearchResultSkeleton />;
    } else if (isEmpty) {
      return <NoProvidersFound />;
    } else {
      if (hasResultKeySet(searchResultSet)) {
        return searchResultSet.map(
          (item: ProviderSearchResultItemType, index: number) => {
            return (
              <ProviderDetails
                key={`provider-search-result-${index}`}
                index={index}
                providerDetail={item}
                openInMapview={openSelectedProviderInMapview}
                getDirection={getDirection}
                callProvider={callProvider}
              />
            );
          },
        );
      } else {
        return <NoProvidersFound />;
      }
    }
  };

  const onFreeTextResultSelect = (
    selected: SpecialtyFreeTextType | NPIFreeTextType | ProviderFreeTextType,
  ) => {
    if (
      (selected as SpecialtyFreeTextType).type ===
      PROVIDER_SETTING_TYPE.SEARCH_BY_CATEGORY
    ) {
      dispatch(
        setProviderSearchDynamicPayload({
          category: (selected as SpecialtyFreeTextType).categoryCode,
          speciality: (selected as SpecialtyFreeTextType).searchParameter,
          specialityDescription: (selected as SpecialtyFreeTextType)
            .specialtyDesc,
        }),
      );
    } else {
      dispatch(
        setProviderSearchDynamicPayload({
          searchCriteria: (
            (selected as NPIFreeTextType) || (selected as ProviderFreeTextType)
          ).npi,
        }),
      );
    }
    dispatch(
      setViewMoreDialog({
        isVisible: false,
        searchBy: "",
        viewMoreSearchCriteria: "",
      }),
    );
    queryClient.removeQueries({
      queryKey: [QueryKeys.PROVIDER_SEARCH_FREE_TEXT],
    });
    queryClient.removeQueries({
      queryKey: [QueryKeys.PROVIDER_SEARCH_FREE_TEXT_VIEW_MORE],
    });
  };

  const toggleMapView = () => {
    setOpenMapView({
      isMapViewOpen: true,
      index: null,
    });
  };

  const getMarkers = (index: number | null) => {
    // console.log("map index", index);

    const allMarkers = providerSearchResult?.pages?.flatMap((obj) => {
      return obj.results.map((result: ProviderSearchResultItemType) => {
        const marker: PlaceMarker = {
          name: result.name,
          address: result.address,
          distance: result.distance,
          pinSymbol: result.pinSymbol,
          color: result.color,
          lat: result.lat,
          lng: result.lon,
        };
        return marker;
      });
    });

    if (index == null) {
      return allMarkers;
    } else {
      return [allMarkers[index]];
    }
  };

  return (
    <PageContainer title="Provider Search Results" enableBackButton={true}>
      <section className="p-6">
        <div className="grid gap-4 sm:grid-cols-3">
          {/* Search by Provider, Specialty or Type (Free Text Search) */}
          <GroupedSearchAutocomplete
            placeholder="E.g. X-ray or Hospital"
            label="Search by Provider (Name or NPI), Specialty, or Type"
            queryFunction={(query) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              return useFreeTextSearch(
                {
                  resultPageSearchCriteria: query,
                  category: category?.categoryCode ?? "",
                  taxonomyCode,
                  speciality: specialty.searchParameter,
                  searchCriteria,
                },
                settingData!,
                searchType,
              );
            }}
            cacheQueryKey={[QueryKeys.PROVIDER_SEARCH_FREE_TEXT]}
            disabled={searchType === null ? true : false}
            formatResult={(sectionKey, item) => {
              if (sectionKey === PROVIDER_SEARCH_FREETEXT_TYPE.NPI) {
                const i = item as NPIFreeTextType;
                return (
                  <div className="w-full">
                    <div className="flex cursor-pointer items-center justify-between bg-white p-0.5">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-foreground">
                          {" "}
                          {capitalizeInitialLetter(i.name)}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          NPI: {i.npi}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {i.distance < 1
                              ? Math.round(i.distance * 10) / 10
                              : Math.round(i.distance)}{" "}
                            miles{" "}
                          </p>
                          <p className="text-xs text-gray-500">(approx.)</p>
                        </div>
                        <ChevronRight className="size-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              }
              if (sectionKey === PROVIDER_SEARCH_FREETEXT_TYPE.PROVIDER) {
                const i = item as ProviderFreeTextType;
                return (
                  <div className="w-full">
                    <div className="flex cursor-pointer items-center justify-between bg-white p-0.5">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-foreground">
                          {" "}
                          {capitalizeInitialLetter(i.name)}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          NPI: {i.npi}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {i.distance < 1
                              ? Math.round(i.distance * 10) / 10
                              : Math.round(i.distance)}{" "}
                            miles{" "}
                          </p>
                          <p className="text-xs text-gray-500">(approx.)</p>
                        </div>
                        <ChevronRight className="size-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              }
              if (sectionKey === PROVIDER_SEARCH_FREETEXT_TYPE.SPECIALITY) {
                const i = item as SpecialtyFreeTextType;
                return (
                  <div>
                    <div className="text-medium font-normal">
                      {i.specialtyDesc}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Category: {i.categoryDesc}
                    </div>
                  </div>
                );
              }
              return null;
            }}
            onLoadMore={(section, searchQuery) => {
              dispatch(
                setViewMoreDialog({
                  isVisible: true,
                  searchBy: section.title,
                  viewMoreSearchCriteria: searchQuery,
                }),
              );
            }}
            onSelect={(item) => onFreeTextResultSelect(item)}
          />
          {/* Location  */}
          <div className="space-y-2">
            <SearchAutocomplete<Place, { queryString: string }>
              label="Location"
              placeholder="Enter a location"
              placeholderIcon={MapPin}
              value={location?.description}
              queryFunction={locationSearchQuery}
              onSelect={async (value: Place) => {
                dispatch(setLocation(value));
              }}
              formatResult={(result: Place, _) => {
                return (
                  <div key={result.place_id}>
                    <div className="text-sm font-medium">
                      {result.description}
                    </div>
                    {/* <div className="text-sm font-medium">
                        {`Lat: ${result.lat.toFixed(4)}, Lng: ${result.lng.toFixed(4)}`}
                      </div> */}
                  </div>
                );
              }}
              onClear={() => {
                dispatch(setLocation(null));
              }}
              minSearchLength={2}
              debounceTime={200}
            />
          </div>
          {/* Distance */}
          <div className="space-y-2">
            <Label>Distance</Label>
            <Dropdown<Distance>
              data={DISTANCE_OPTIONS}
              value={distance}
              placeholder="Select a Distance"
              multiple={false}
              renderItem={(item) => <span>{item.displayName}</span>}
              getItemValue={(item) => distance && item?.displayName}
              onChange={(selectedDistance) => {
                if (selectedDistance) {
                  dispatch(setDistance(selectedDistance as Distance));
                }
              }}
            />
          </div>
        </div>
      </section>
      <section className="">
        <section>
          {isLoading ? (
            <div className="flex w-full items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-28 animate-pulse rounded-lg bg-gray-300" />
              </div>
            </div>
          ) : (
            <div className="flex w-full items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Button
                  className="rounded-lg bg-primary px-4 py-2 text-white hover:bg-primary-selected"
                  onClick={toggleMapView}
                >
                  <Map className="mr-2 size-4" />
                  Map View
                </Button>
              </div>
            </div>
          )}
        </section>
        <section className="space-y-6 p-6">
          {renderSearchResult()}
          {isFetching && !isLoading && <ProviderSearchResultSkeleton />}
          <div ref={loadMoreRef} className="h-2" />
        </section>
      </section>
      {/* <ProviderSearchResultSkeleton /> */}
      {viewMoreDialog?.isVisible ? (
        <ProviderSearchFreeTextViewMore onSelect={onFreeTextResultSelect} />
      ) : null}
      <RightSidebar
        title="Map View"
        isOpen={openMapView.isMapViewOpen}
        onClose={() =>
          setOpenMapView({
            isMapViewOpen: false,
            index: null,
          })
        }
      >
        {providerSearchResult && openMapView.isMapViewOpen ? (
          <MapView searchResult={getMarkers(openMapView.index)} />
        ) : null}
      </RightSidebar>
    </PageContainer>
  );
};

export default ProviderSearchResult;
